import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import useUrlParams from "../../utils/useURLParams";
import firebaseService from "../../utils/firebase";

const COLORS = {
    green: '#0b9e2b',
    greenSoft: '#e2f5e7',
    text: '#0f172a',
    mutedText: '#475467',
    border: '#e6e6e6',
    cardBg: '#ffffff',
    progressBg: '#e6e6e6'
};

const BannerLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  max-width: 640px;
  margin: 0 auto;
  padding: 16px 20px;
  border-radius: 14px;
  background: ${COLORS.cardBg};
  color: ${COLORS.text};
  border: 1px solid ${COLORS.border};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 16px;
  color: ${COLORS.text};
`;

const Title = styled.span`
  flex: 1;
  min-width: 0;
  line-height: 1.25;
`;

const SupportBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${COLORS.cardBg};
  border: 1px solid ${COLORS.green};
  color: ${COLORS.green};
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);

  ${BannerLink}:hover &,
  ${BannerLink}:focus-visible & {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.14);
  }
`;

const Highlight = styled.span`
  color: ${COLORS.green};
  font-weight: 700;
`;

const ProgressBar = styled.div`
  width: 100%;
  background: ${COLORS.progressBg};
  border-radius: 999px;
  overflow: hidden;
  height: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({progress}) => progress}%;
  background: ${COLORS.green};
  transition: width 0.3s ease;
`;

const Footer = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: ${COLORS.mutedText};
`;

const copy = {
    ru: {
        support: 'Поддержать',
        of: 'из',
        goal: 'цели'
    },
    be: {
        support: 'Падтрымаць',
        of: 'з',
        goal: 'мэты'
    },
    en: {
        support: 'Support',
        of: 'of',
        goal: 'goal'
    }
};

const formatAmount = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString('ru-RU', {maximumFractionDigits: 0});
};

const Banner = () => {
    const urlParams = useUrlParams();
    const bucketId = urlParams?.bucket;
    const lng = useMemo(() => {
        const language = (urlParams?.lng || '').toLowerCase();
        if (['ru', 'be', 'en'].includes(language)) return language;
        return 'ru';
    }, [urlParams?.lng]);

    const [bucket, setBucket] = useState({value: 0, goal: 0});
    const [title, setTitle] = useState('');
    const [bucketType, setBucketType] = useState('bs');
    const [siteId, setSiteId] = useState(bucketId);

    const handleBucketSnapshot = useCallback((data) => {
        const metadata = data?.metadata || {};
        setBucket({
            value: data?.value || 0,
            goal: data?.goal || 0,
        });
        setBucketType(metadata?.type || 'bs');
        setSiteId(metadata?.siteId || bucketId || '');
        if (data) {
            const ruTitle = metadata?.ruTitle || '';
            const beTitle = metadata?.beTitle || '';
            const enTitle = metadata?.enTitle || '';
            if (lng === 'en') {
                setTitle(enTitle || ruTitle || beTitle);
            } else if (lng === 'be') {
                setTitle(beTitle || ruTitle || enTitle);
            } else {
                setTitle(ruTitle || beTitle || enTitle);
            }
        }
    }, [bucketId, lng]);

    useEffect(() => {
        if (!bucketId) return;

        const unsubscribe = firebaseService.getBucket(bucketId, handleBucketSnapshot);

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [bucketId, handleBucketSnapshot]);

    const progress = useMemo(() => {
        if (!bucket.goal || bucket.goal <= 0) return 0;
        const percent = (bucket.value / bucket.goal) * 100;
        return Math.min(Math.max(percent, 0), 100);
    }, [bucket.goal, bucket.value]);

    const targetUrl = useMemo(() => {
        return `https://bysol.org/${lng}/${bucketType}/${siteId}`;
    }, [lng, bucketType, siteId]);

    const {support, of, goal} = copy[lng];

    if (!bucketId) return null;

    return (
        <BannerLink href={targetUrl} target="_blank" rel="noopener noreferrer">
            <Wrapper>
                <Header>
                    <Title>{title}</Title>
                    <SupportBadge>{support}</SupportBadge>
                </Header>
                <ProgressBar>
                    <ProgressFill progress={progress}/>
                </ProgressBar>
                <Footer>
                    <span><Highlight>€{formatAmount(Math.floor(bucket.value))}</Highlight></span>
                    <span>
                        {of} <Highlight>€{formatAmount(Math.floor(bucket.goal))}</Highlight> {goal}
                    </span>
                </Footer>
            </Wrapper>
        </BannerLink>
    );
};

export default Banner;
